/**
 * TrendIndicator Component
 * Visual indicator for metric trends
 */

import React from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    MinusOutlined
} from '@ant-design/icons';

const IndicatorContainer = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    background: ${props => {
        switch (props.trend) {
            case 'improving':
                return '#f6ffed';
            case 'declining':
                return '#fff1f0';
            case 'stable':
            default:
                return '#f5f5f5';
        }
    }};
    color: ${props => {
        switch (props.trend) {
            case 'improving':
                return '#52c41a';
            case 'declining':
                return '#f5222d';
            case 'stable':
            default:
                return '#8c8c8c';
        }
    }};
    border: 1px solid ${props => {
        switch (props.trend) {
            case 'improving':
                return '#b7eb8f';
            case 'declining':
                return '#ffa39e';
            case 'stable':
            default:
                return '#d9d9d9';
        }
    }};
`;

const SignificanceIndicator = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-left: 4px;
    background: ${props => {
        switch (props.significance) {
            case 'high':
                return '#f5222d';
            case 'medium':
                return '#faad14';
            case 'low':
            default:
                return '#52c41a';
        }
    }};
`;

const getTrendIcon = (trend) => {
    switch (trend) {
        case 'improving':
            return <ArrowUpOutlined />;
        case 'declining':
            return <ArrowDownOutlined />;
        case 'stable':
        default:
            return <MinusOutlined />;
    }
};

const getTrendLabel = (trend) => {
    switch (trend) {
        case 'improving':
            return 'Improving';
        case 'declining':
            return 'Declining';
        case 'stable':
        default:
            return 'Stable';
    }
};

const getSignificanceLabel = (significance) => {
    switch (significance) {
        case 'high':
            return 'High significance';
        case 'medium':
            return 'Medium significance';
        case 'low':
        default:
            return 'Low significance';
    }
};

/**
 * TrendIndicator Component
 * @param {Object} props - Component props
 * @param {string} props.trend - Trend direction (improving, declining, stable)
 * @param {string} props.significance - Significance level (high, medium, low)
 */
function TrendIndicator({ trend, significance }) {
    const tooltipText = `${getTrendLabel(trend)}${significance ? ` - ${getSignificanceLabel(significance)}` : ''}`;

    return (
        <Tooltip title={tooltipText}>
            <IndicatorContainer trend={trend}>
                {getTrendIcon(trend)}
                <span style={{ marginLeft: 4 }}>{getTrendLabel(trend)}</span>
                {significance && (
                    <SignificanceIndicator significance={significance} />
                )}
            </IndicatorContainer>
        </Tooltip>
    );
}

export default TrendIndicator;
